import React, { useState, useEffect } from 'react';
import { 
  Users, FileText, Mail, Phone, Calendar, MapPin, Building2, 
  Filter, Search, Download, ChevronDown, ChevronUp, Eye, Trash2,
  RefreshCw, AlertCircle, CheckCircle, Clock, DollarSign
} from 'lucide-react';
import { LOGO_NAVBAR } from '../constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple auth - in production use proper authentication
const ADMIN_PASSWORD = 'nexair2026';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);

  // Authentication
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      localStorage.setItem('nexair_admin_auth', 'true');
    } else {
      setAuthError('Contraseña incorrecta');
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem('nexair_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/quotes`);
      if (response.ok) {
        const data = await response.json();
        // Handle both array response and {quotes: [...]} format
        setQuotes(Array.isArray(data) ? data : (data.quotes || []));
      }
    } catch (err) {
      setError('Error al cargar cotizaciones');
    }
    setLoading(false);
  };

  const fetchSimulations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/simulations`);
      if (response.ok) {
        const data = await response.json();
        // Handle both array response and {simulations: [...]} format
        setSimulations(Array.isArray(data) ? data : (data.simulations || []));
      }
    } catch (err) {
      setError('Error al cargar simulaciones');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuotes();
      fetchSimulations();
    }
  }, [isAuthenticated]);

  // Filter and sort data
  const filterData = (data) => {
    return data
      .filter(item => {
        const matchesSearch = 
          (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (item.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (item.company?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (item.project_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        
        const matchesService = filterService === 'all' || 
          item.service_type === filterService ||
          item.project_type === filterService;
        
        return matchesSearch && matchesService;
      })
      .sort((a, b) => {
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const exportCSV = () => {
    const data = activeTab === 'quotes' ? filterData(quotes) : filterData(simulations);
    const headers = activeTab === 'quotes' 
      ? ['Fecha', 'Nombre', 'Email', 'Teléfono', 'Empresa', 'Tipo Evento', 'Servicio', 'Ubicación', 'Asistentes', 'Mensaje']
      : ['Fecha', 'Proyecto', 'Tipo', 'Email', 'WhatsApp', 'Presupuesto', 'Ubicación', 'Fecha Evento'];
    
    const rows = data.map(item => {
      if (activeTab === 'quotes') {
        return [
          formatDate(item.created_at),
          item.name || '',
          item.email || '',
          item.phone || '',
          item.company || '',
          item.event_type || '',
          item.service_type || '',
          item.location || '',
          item.attendees || '',
          (item.message || '').replace(/,/g, ';')
        ];
      }
      return [
        formatDate(item.created_at),
        item.project_name || '',
        item.project_type || '',
        item.email || '',
        item.whatsapp || '',
        item.budget || '',
        item.location || '',
        item.date || ''
      ];
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `nexair_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#030810] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src={LOGO_NAVBAR} alt="NexAir" className="h-10 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white font-heading">Panel de Administración</h1>
            <p className="text-gray-400 mt-2">Ingresa la contraseña para acceder</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 mb-4"
            />
            {authError && (
              <p className="text-red-400 text-sm mb-4 flex items-center gap-2">
                <AlertCircle size={16} /> {authError}
              </p>
            )}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity"
            >
              Acceder
            </button>
          </form>
        </div>
      </div>
    );
  }

  const currentData = activeTab === 'quotes' ? filterData(quotes) : filterData(simulations);

  return (
    <div className="min-h-screen bg-[#030810]">
      {/* Header */}
      <header className="bg-black/50 border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_NAVBAR} alt="NexAir" className="h-8" />
            <span className="text-white font-heading text-lg">Admin Panel</span>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('nexair_admin_auth');
              setIsAuthenticated(false);
            }}
            className="text-gray-400 hover:text-white text-sm"
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <span className="text-gray-400 text-sm">Cotizaciones</span>
            </div>
            <p className="text-3xl font-bold text-white">{quotes.length}</p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-gray-400 text-sm">Simulaciones</span>
            </div>
            <p className="text-3xl font-bold text-white">{simulations.length}</p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-gray-400 text-sm">Esta semana</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {[...quotes, ...simulations].filter(item => {
                const date = new Date(item.created_at);
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return date > weekAgo;
              }).length}
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
              <span className="text-gray-400 text-sm">Último lead</span>
            </div>
            <p className="text-sm font-medium text-white">
              {[...quotes, ...simulations]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.created_at 
                ? formatDate([...quotes, ...simulations].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0].created_at)
                : '-'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('quotes')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'quotes' 
                ? 'bg-cyan-500 text-white' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Cotizaciones ({quotes.length})
          </button>
          <button
            onClick={() => setActiveTab('simulations')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'simulations' 
                ? 'bg-purple-500 text-white' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Simulaciones ({simulations.length})
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email, empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>
            
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500"
            >
              <option value="all">Todos los servicios</option>
              <option value="Show de Drones">Show de Drones</option>
              <option value="Pantalla de Agua">Pantalla de Agua</option>
              <option value="Efectos Especiales">Efectos Especiales</option>
              <option value="Elements">Elements</option>
            </select>
            
            <button
              onClick={() => { fetchQuotes(); fetchSimulations(); }}
              className="flex items-center gap-2 bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-gray-400 hover:text-white hover:border-white/40 transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 bg-cyan-500/20 border border-cyan-500/30 rounded-lg px-4 py-2 text-cyan-400 hover:bg-cyan-500/30 transition-all"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  {activeTab === 'quotes' ? (
                    <>
                      <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>
                        Fecha {sortField === 'created_at' && (sortDirection === 'asc' ? <ChevronUp className="inline w-4 h-4" /> : <ChevronDown className="inline w-4 h-4" />)}
                      </th>
                      <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('name')}>
                        Nombre {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp className="inline w-4 h-4" /> : <ChevronDown className="inline w-4 h-4" />)}
                      </th>
                      <th className="text-left p-4 text-gray-400 font-medium">Contacto</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Empresa</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Servicio</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Evento</th>
                      <th className="text-left p-4 text-gray-400 font-medium w-12"></th>
                    </>
                  ) : (
                    <>
                      <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>
                        Fecha {sortField === 'created_at' && (sortDirection === 'asc' ? <ChevronUp className="inline w-4 h-4" /> : <ChevronDown className="inline w-4 h-4" />)}
                      </th>
                      <th className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('project_name')}>
                        Proyecto {sortField === 'project_name' && (sortDirection === 'asc' ? <ChevronUp className="inline w-4 h-4" /> : <ChevronDown className="inline w-4 h-4" />)}
                      </th>
                      <th className="text-left p-4 text-gray-400 font-medium">Contacto</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Tipo</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Presupuesto</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Fecha Evento</th>
                      <th className="text-left p-4 text-gray-400 font-medium w-12"></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Cargando...
                    </td>
                  </tr>
                ) : currentData.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-500">
                      No hay registros
                    </td>
                  </tr>
                ) : (
                  currentData.map((item, index) => (
                    <React.Fragment key={item.id || index}>
                      <tr 
                        className={`border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${expandedRow === index ? 'bg-white/5' : ''}`}
                        onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                      >
                        {activeTab === 'quotes' ? (
                          <>
                            <td className="p-4 text-gray-300 text-sm">{formatDate(item.created_at)}</td>
                            <td className="p-4 text-white font-medium">{item.name || '-'}</td>
                            <td className="p-4">
                              <div className="flex flex-col gap-1">
                                <span className="text-cyan-400 text-sm">{item.email}</span>
                                {item.phone && <span className="text-gray-500 text-xs">{item.phone}</span>}
                              </div>
                            </td>
                            <td className="p-4 text-gray-300">{item.company || '-'}</td>
                            <td className="p-4">
                              <span className="px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">
                                {item.service_type || 'General'}
                              </span>
                            </td>
                            <td className="p-4 text-gray-300">{item.event_type || '-'}</td>
                            <td className="p-4">
                              {expandedRow === index ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="p-4 text-gray-300 text-sm">{formatDate(item.created_at)}</td>
                            <td className="p-4 text-white font-medium">{item.project_name || '-'}</td>
                            <td className="p-4">
                              <div className="flex flex-col gap-1">
                                <span className="text-purple-400 text-sm">{item.email}</span>
                                {item.whatsapp && <span className="text-gray-500 text-xs">{item.whatsapp}</span>}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                                {item.project_type || 'General'}
                              </span>
                            </td>
                            <td className="p-4 text-gray-300">{item.budget || '-'}</td>
                            <td className="p-4 text-gray-300">{item.date || '-'}</td>
                            <td className="p-4">
                              {expandedRow === index ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                            </td>
                          </>
                        )}
                      </tr>
                      {/* Expanded details */}
                      {expandedRow === index && (
                        <tr className="bg-white/[0.02]">
                          <td colSpan={7} className="p-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              {activeTab === 'quotes' ? (
                                <>
                                  <div>
                                    <h4 className="text-white font-medium mb-3">Detalles del Evento</h4>
                                    <div className="space-y-2 text-sm">
                                      <p><span className="text-gray-500">Fecha del evento:</span> <span className="text-gray-300">{item.event_date || '-'}</span></p>
                                      <p><span className="text-gray-500">Ubicación:</span> <span className="text-gray-300">{item.location || '-'}</span></p>
                                      <p><span className="text-gray-500">Asistentes:</span> <span className="text-gray-300">{item.attendees || '-'}</span></p>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="text-white font-medium mb-3">Mensaje</h4>
                                    <p className="text-gray-400 text-sm bg-white/5 rounded-lg p-4">
                                      {item.message || 'Sin mensaje adicional'}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <h4 className="text-white font-medium mb-3">Detalles del Proyecto</h4>
                                    <div className="space-y-2 text-sm">
                                      <p><span className="text-gray-500">Ubicación:</span> <span className="text-gray-300">{item.location || '-'}</span></p>
                                      <p><span className="text-gray-500">Fecha:</span> <span className="text-gray-300">{item.date || '-'}</span></p>
                                      <p><span className="text-gray-500">Presupuesto:</span> <span className="text-gray-300">{item.budget || '-'}</span></p>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="text-white font-medium mb-3">Archivo Adjunto</h4>
                                    {item.file_name ? (
                                      <p className="text-cyan-400 text-sm">{item.file_name}</p>
                                    ) : (
                                      <p className="text-gray-500 text-sm">Sin archivo</p>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
                              <a 
                                href={`mailto:${item.email}`}
                                className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 transition-all"
                              >
                                <Mail className="w-4 h-4" />
                                Enviar Email
                              </a>
                              {(item.phone || item.whatsapp) && (
                                <a 
                                  href={`https://wa.me/${(item.phone || item.whatsapp).replace(/\D/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30 transition-all"
                                >
                                  <Phone className="w-4 h-4" />
                                  WhatsApp
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination info */}
        <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
          <span>Mostrando {currentData.length} de {activeTab === 'quotes' ? quotes.length : simulations.length} registros</span>
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
